/**
 * Tenant Cleanup Service
 *
 * API client for stale-tenant cleanup endpoints (scan / archive / purge).
 * Part of UC-018: Stale Tenant Cleanup (GDPR Admin Tool).
 *
 * Builds against the §5 API contract under `/api/gdpradm/tenants/*`.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3050/api';

/**
 * Scan for stale tenant candidates (read-only, no side effects).
 * @returns {Promise<{success: boolean, candidates: Array}>}
 */
export const scanStale = async () => {
  const response = await fetch(`${API_URL}/gdpradm/tenants/scan-stale`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to scan for stale tenants');
  }

  return response.json();
};

/**
 * Archive (phase 1, reversible) or unarchive a tenant.
 * @param {string} orgId - Organization ID (e.g., HCS0004)
 * @param {boolean} [undo=false] - When true, unarchive (STATUS back to ACTIVE)
 * @returns {Promise<{success: boolean, org_id: string, previous_status: string, new_status: string}>}
 */
export const archiveTenant = async (orgId, undo = false) => {
  const params = new URLSearchParams();
  if (undo) params.append('undo', 'true');
  const query = params.toString();

  const response = await fetch(
    `${API_URL}/gdpradm/tenants/${orgId}/archive${query ? `?${query}` : ''}`,
    { method: 'POST' }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `Failed to ${undo ? 'unarchive' : 'archive'} tenant`);
  }

  return response.json();
};

/**
 * Purge (phase 2, destructive) a tenant. Dry-run first, then confirm.
 * @param {string} orgId - Organization ID
 * @param {Object} options
 * @param {boolean} options.dryRun - When true, returns planned objects only (no mutation)
 * @param {string} [options.confirmToken] - Must equal org_id to execute (dryRun=false)
 * @param {string} [options.adminPassword] - GDPR admin password, re-verified server-side
 *   on the destructive call only (harmless/ignored on dry-run)
 * @returns {Promise<Object>} { success, org_id, dry_run, planned_or_executed, guardrail, confirmation_number }
 */
export const purgeTenant = async (orgId, { dryRun, confirmToken, adminPassword } = {}) => {
  const response = await fetch(`${API_URL}/gdpradm/tenants/${orgId}/purge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      dry_run: dryRun,
      confirm_token: confirmToken || '',
      admin_password: adminPassword,
    }),
  });

  const data = await response.json().catch(() => ({ detail: 'Request failed' }));

  if (!response.ok) {
    throw new Error(data.detail || 'Failed to purge tenant');
  }

  return data;
};

export default {
  scanStale,
  archiveTenant,
  purgeTenant,
};
